import React, { useState } from 'react';
import { Box, Typography, Button, useTheme, useMediaQuery, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colors, fonts } from '../../ColorFont/color';
import { url } from '../../ConstUrl/Url';
import {
    IconLeaf,
    IconDroplets,
    IconThermometer,
    IconTrees,
    IconFlameOff,
    IconSunWind,
    IconCheck
} from '@tabler/icons-react';
import NewHeaderNavWithSidebar from '../../components/HeaderNav/NewHeaderNavWithSidebar';

/* 
===== UNIVERSAL TOKENS =====
carbon_bg_main: #00B4D8
carbon_bg_selected: #E0F7FA
carbon_border_selected: #00B4D8
carbon_text_credits: #0097A7
carbon_bg_estimated: #E0F7FA
carbon_border_estimated: #80DEEA
carbon_text_estimated_title: #00838F
============================
*/

const translations = {
    en: {
        practices: "Practices",
        sustainablePractices: "Sustainable Practices",
        selectPractices: "Select practices you follow",
        organicFarming: "Organic Farming",
        organicFarmingDesc: "No synthetic chemicals",
        reducedFertilizer: "Reduced Fertilizer",
        reducedFertilizerDesc: "50%+ reduction in chemical fertilizers",
        noTillFarming: "No-Till Farming",
        noTillFarmingDesc: "Minimal soil disturbance",
        dripIrrigation: "Drip Irrigation",
        dripIrrigationDesc: "Water-efficient irrigation",
        agroforestry: "Agroforestry",
        agroforestryDesc: "Trees integrated with crops",
        noStubbleBurning: "No Stubble Burning",
        noStubbleBurningDesc: "Crop residue management",
        renewableEnergy: "Renewable Energy",
        renewableEnergyDesc: "Solar/wind power usage",
        estimatedCarbonCredits: "Estimated Carbon Credits",
        creditsAcre: "credits/acre",
        tCo2: "tCO₂e",
        saveAndContinue: "Save and Continue",
        back: "Back"
    },
    mr: {
        practices: "पद्धती",
        sustainablePractices: "शाश्वत पद्धती",
        selectPractices: "तुम्ही पाळत असलेल्या पद्धती निवडा",
        organicFarming: "सेंद्रिय शेती",
        organicFarmingDesc: "कोणतेही कृत्रिम रसायने नाहीत",
        reducedFertilizer: "कमी खते",
        reducedFertilizerDesc: "रासायनिक खतांमध्ये ५०%+ कपात",
        noTillFarming: "विना नांगरणी शेती",
        noTillFarmingDesc: "कमीत कमी मातीची नासाडी",
        dripIrrigation: "ठिबक सिंचन",
        dripIrrigationDesc: "पाण्याची बचत करणारे सिंचन",
        agroforestry: "कृषी वनीकरण",
        agroforestryDesc: "पिकांसह झाडे",
        noStubbleBurning: "पीक अवशेष न जाळणे",
        noStubbleBurningDesc: "पीक अवशेष व्यवस्थापन",
        renewableEnergy: "अक्षय ऊर्जा",
        renewableEnergyDesc: "सौर/पवन ऊर्जेचा वापर",
        estimatedCarbonCredits: "अंदाजित कार्बन क्रेडिट्स",
        creditsAcre: "क्रेडिट्स/एकर",
        tCo2: "tCO₂e",
        saveAndContinue: "जतन करा आणि पुढे जा",
        back: "मागे"
    },
    hi: {
        practices: "अभ्यास",
        sustainablePractices: "सतत अभ्यास",
        selectPractices: "आपण किन प्रथाओं का पालन करते हैं",
        organicFarming: "जैविक खेती",
        organicFarmingDesc: "कोई सिंथेटिक रसायन नहीं",
        reducedFertilizer: "कम उर्वरक",
        reducedFertilizerDesc: "रासायनिक उर्वरकों में 50%+ की कमी",
        noTillFarming: "नो-टिल्ल फार्मिंग",
        noTillFarmingDesc: "न्यूनतम मिट्टी की गड़बड़ी",
        dripIrrigation: "टपकन सिंचाई",
        dripIrrigationDesc: "जल-कुशल सिंचाई",
        agroforestry: "कृषि वानिकी",
        agroforestryDesc: "फसलों के साथ पेड़",
        noStubbleBurning: "पराली न जलाना",
        noStubbleBurningDesc: "फसल अवशेष प्रबंधन",
        renewableEnergy: "अक्षय ऊर्जा",
        renewableEnergyDesc: "सौर/पवन ऊर्जा का उपयोग",
        estimatedCarbonCredits: "अनुमानित कार्बन क्रेडिट",
        creditsAcre: "क्रेडिट्स/एकड़",
        tCo2: "tCO₂e",
        saveAndContinue: "सहेजें और आगे बढ़ें",
        back: "वापस"
    }
};

// Practice options will be fetched from backend
// const PRACTICE_OPTIONS = [...];

const ICON_MAP = {
    'Organic Farming': IconLeaf,
    'Reduced Fertilizer': IconDroplets,
    'No-Till Farming': IconThermometer,
    'Drip Irrigation': IconDroplets,
    'Agroforestry': IconTrees,
    'No Stubble Burning': IconFlameOff,
    'Renewable Energy': IconSunWind
};

const CarbonCreditsPractices = ({ userData, onNext, onBack }) => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language || 'en';
    const tLocal = translations[currentLang] || translations['en'];
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [selectedPractices, setSelectedPractices] = useState([]);
    const [practices, setPractices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    React.useEffect(() => {
        const fetchPractices = async () => {
            try {
                const response = await fetch(`${url}/api/carbon-credits/practices`);
                const data = await response.json();
                if (response.ok) {
                    setPractices(data.practices);
                    // Pre-select first practice if available and none selected
                    if (data.practices.length > 0) {
                        setSelectedPractices([data.practices[0].name]);
                    }
                }
            } catch (error) {
                console.error("Error fetching practices:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchPractices();
    }, []);

    const steps = [
        { id: 1, label: "", isActive: true, isCompleted: true },
        { id: 2, label: tLocal.practices, isActive: true, isCompleted: false },
        { id: 3, label: "", isActive: false, isCompleted: false },
        { id: 4, label: "", isActive: false, isCompleted: false },
    ];

    const togglePractice = (id) => {
        setSelectedPractices(prev => {
            if (prev.includes(id)) {
                return prev.filter(p => p !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    const [unitInfo, setUnitInfo] = useState({ name: 'Acres', factor: 1 });

    React.useEffect(() => {
        const draft = JSON.parse(localStorage.getItem('carbonCreditDraft') || '{}');
        const unitFactors = draft.unitFactors || [];
        const selectedUnit = draft.unit || 'Acres';
        const unitFactorObj = unitFactors.find(u => u.name === selectedUnit);
        if (unitFactorObj) {
            setUnitInfo(unitFactorObj);
        }
    }, [loading]);

    const calculateEstimatedCredits = () => {
        const draft = JSON.parse(localStorage.getItem('carbonCreditDraft') || '{}');
        const landSize = parseFloat(draft.landSize) || 0;
        const factor = unitInfo.factor;

        let rateSum = 0;
        practices.forEach(practice => {
            if (selectedPractices.includes(practice.name)) {
                rateSum += practice.creditsPerUnit || 0;
            }
        });
        
        const total = rateSum * factor * landSize;
        return total.toFixed(1); 
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            const draft = JSON.parse(localStorage.getItem('carbonCreditDraft') || '{}');
            draft.practices = selectedPractices;
            draft.estimatedCredits = calculateEstimatedCredits();
            localStorage.setItem('carbonCreditDraft', JSON.stringify(draft));

            if (onNext) onNext();
        } catch (error) {
            console.error("Error saving draft info:", error);
            alert("An error occurred while saving locally.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box sx={{
            width: '100%',
            minHeight: '100vh',
            backgroundColor: '#FFFFFF',
            pb: '80px'
        }}>
            {/* Top Stepper Area (Takes full width) */}
            <Box sx={{
                width: '100%',
                pt: isMobile ? '12px' : '16px',
                pb: isMobile ? '12px' : '14px',
                borderBottom: `1px solid ${colors.carbon_border_solid || '#E0E5EB'}`,
                backgroundColor: '#FFFFFF',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center', // Centered vertically
                    justifyContent: 'space-between',
                    width: '100%',
                    maxWidth: '680px',
                    px: isMobile ? '20px' : '24px'
                }}>
                    {steps.map((step, index) => (
                        <React.Fragment key={step.id}>
                            <Box sx={{
                                width: '32px',
                                height: '32px',
                                flexShrink: 0,
                                borderRadius: '50%',
                                backgroundColor: (step.isActive || step.isCompleted) ? (colors.carbon_bg_main || '#00B4D8') : (colors.carbon_step_bg_unselected || '#E2E8F0'),
                                color: (step.isActive || step.isCompleted) ? '#FFFFFF' : (colors.carbon_step_text_unselected || '#64748B'),
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontWeight: 600,
                                fontSize: '14px',
                                zIndex: 2
                            }}>
                                {step.isCompleted ? <IconCheck size={18} color="#FFFFFF" stroke={2.5} /> : step.id}
                            </Box>

                            {index < steps.length - 1 && (
                                <Box sx={{
                                    flex: 1, // Stretch precisely
                                    mx: isMobile ? '8px' : '16px',
                                    height: '2px', // Thinner cleaner line
                                    backgroundColor: (steps[index + 1].isActive || steps[index + 1].isCompleted) ? (colors.carbon_bg_main || '#00B4D8') : (colors.carbon_step_bg_unselected || '#E2E8F0'),
                                    borderRadius: '4px'
                                }} />
                            )}
                        </React.Fragment>
                    ))}
                </Box>
                <Typography sx={{
                    mt: '16px',
                    fontFamily: fonts.publicSans || fonts.poppins,
                    fontWeight: 500,
                    fontSize: '13px',
                    color: colors.carbon_text_secondary || '#65758B',
                }}>
                    {steps.find(s => s.label)?.label || ""}
                </Typography>
            </Box>

            {/* Farm Practices Content Area */}
            <Box sx={{
                maxWidth: '680px', // Center layout constraint
                margin: '0 auto',
                px: 0,
                pt: isMobile ? '16px' : '24px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
            }}>
                {/* Header Icon & Title */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    mb: '24px'
                }}>
                    <Box sx={{ color: colors.carbon_bg_main || '#00B4D8' }}>
                        <IconLeaf size={48} stroke={1.5} />
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={{
                            fontFamily: fonts.manrope || fonts.poppins,
                            fontWeight: 700,
                            fontSize: isMobile ? '24px' : '28px',
                            lineHeight: '34px',
                            color: colors.carbon_text_main || '#0F1729',
                            mb: '8px'
                        }}>
                            {tLocal.sustainablePractices}
                        </Typography>
                        <Typography sx={{
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 400,
                            fontSize: isMobile ? '14px' : '16px',
                            lineHeight: '22px',
                            color: colors.carbon_text_secondary || '#65758B',
                            maxWidth: '480px'
                        }}>
                            {tLocal.selectPractices}
                        </Typography>
                    </Box>
                </Box>

                {/* Practices List Container */}
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>

                    {/* ===== NEW FIGMA UI: Reusable Card Component ===== */}
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress color="inherit" sx={{ color: colors.carbon_bg_main }} />
                        </Box>
                    ) : practices.map((practice) => {
                        const isSelected = selectedPractices.includes(practice.name);
                        const PracticeIcon = ICON_MAP[practice.name] || IconLeaf;

                        return (
                            <Box
                                key={practice._id}
                                onClick={() => togglePractice(practice.name)}
                                sx={{
                                    width: '100%',
                                    minHeight: '74px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    backgroundColor: isSelected ? (colors.carbon_bg_selected || '#E0F7FA') : '#FFFFFF',
                                    border: isSelected ? `1px solid ${colors.carbon_border_selected || '#00B4D8'}` : `1px solid ${colors.carbon_border_solid || '#E0E5EB'}`,
                                    borderRadius: '12px',
                                    boxShadow: isSelected ? '0px 0px 0px 1px #00B4D8' : '0px 1px 2px -1px rgba(0,0,0,0.1), 0px 1px 3px 0px rgba(0,0,0,0.1)',
                                    cursor: 'pointer',
                                    padding: '16px 20px',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        boxShadow: isSelected ? '0px 0px 0px 1px #00B4D8' : '0px 4px 6px -1px rgba(0,0,0,0.1)',
                                        transform: 'translateY(-1px)'
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    <Box sx={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '12px',
                                        backgroundColor: isSelected ? (colors.carbon_bg_main || '#00B4D8') : '#F1F5F9', // light grey for unselected
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0
                                    }}>
                                        <PracticeIcon size={20} color={isSelected ? '#FFFFFF' : '#64748B'} stroke={2} />
                                    </Box>

                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        <Typography sx={{
                                            fontFamily: fonts.manrope || fonts.poppins,
                                            fontWeight: 600,
                                            fontSize: '16px',
                                            lineHeight: '24px',
                                            letterSpacing: '-0.4px',
                                            color: colors.carbon_text_main || '#0F1729'
                                        }}>
                                            {practice.name}
                                        </Typography>
                                        <Typography sx={{
                                            fontFamily: fonts.publicSans || fonts.poppins,
                                            fontWeight: 400,
                                            fontSize: '12px',
                                            lineHeight: '16px',
                                            color: colors.carbon_text_secondary || '#65758B'
                                        }}>
                                            {practice.description}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                                    <Typography sx={{
                                        fontFamily: fonts.publicSans || fonts.poppins,
                                        fontWeight: 700,
                                        fontSize: '14px',
                                        lineHeight: '20px',
                                        color: colors.carbon_text_credits || '#0097A7',
                                        textAlign: 'right'
                                    }}>
                                        +{(practice.creditsPerUnit * unitInfo.factor).toFixed(1)}
                                    </Typography>
                                    <Typography sx={{
                                        fontFamily: fonts.publicSans || fonts.poppins,
                                        fontWeight: 400,
                                        fontSize: '10px',
                                        lineHeight: '16px',
                                        color: colors.carbon_text_secondary || '#65758B',
                                        textAlign: 'right'
                                    }}>
                                        {tLocal.tCo2} / {unitInfo.name}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })}

                    {/* Estimated Carbon Credits Widget */}
                    <Box sx={{
                        width: '100%',
                        minHeight: '90px',
                        mt: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: colors.carbon_bg_estimated || '#E0F7FA',
                        border: `1px solid ${colors.carbon_border_estimated || '#80DEEA'}`,
                        borderRadius: '12px',
                        boxShadow: '0px 1px 2px -1px rgba(0,0,0,0.1), 0px 1px 3px 0px rgba(0,0,0,0.1)',
                        padding: '16px',
                        gap: '4px'
                    }}>
                        <Typography sx={{
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 400,
                            fontSize: '14px',
                            lineHeight: '20px',
                            color: colors.carbon_text_estimated_title || '#00838F',
                            textAlign: 'center'
                        }}>
                            {tLocal.estimatedCarbonCredits}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                            <Typography sx={{
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontWeight: 700,
                                fontSize: '30px',
                                lineHeight: '36px',
                                color: colors.carbon_text_credits || '#0097A7',
                                textAlign: 'center'
                            }}>
                                {calculateEstimatedCredits()}
                            </Typography>
                             <Typography sx={{
                                 fontFamily: fonts.publicSans || fonts.poppins,
                                 fontWeight: 700,
                                 fontSize: '20px',
                                 lineHeight: '36px',
                                 color: colors.carbon_text_credits || '#0097A7',
                                 textAlign: 'center'
                             }}>
                                 tCO<Typography component="span" sx={{ fontSize: '14px', fontWeight: 700, verticalAlign: 'sub' }}>₂</Typography>e
                             </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Submit Action */}
                <Box sx={{ mt: '48px', width: '100%', display: 'flex', justifyContent: 'center', gap: '16px', flexDirection: isMobile ? 'column' : 'row' }}>
                    <Button
                        onClick={onBack}
                        variant="outlined"
                        sx={{
                            borderRadius: '50px',
                            px: { xs: 3, md: 6 },
                            height: '52px',
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 700,
                            fontSize: '15px',
                            textTransform: 'none',
                            color: colors.carbon_text_main || '#1C252E',
                            borderColor: colors.carbon_border_solid || '#E0E5EB',
                            minWidth: { sm: '180px' },
                            '&:hover': {
                                borderColor: colors.carbon_border_selected || '#919EAB',
                                backgroundColor: '#F4F6F8',
                            },
                        }}
                    >
                        {tLocal.back}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        sx={{
                            borderRadius: '50px',
                            px: { xs: 3, md: 6 },
                            height: '52px',
                            backgroundColor: colors.carbon_bg_main || '#00B4D8',
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 700,
                            fontSize: '15px',
                            textTransform: 'none',
                            color: '#FFFFFF',
                            minWidth: { sm: '220px' },
                            boxShadow: '0px 4px 12px rgba(0, 180, 216, 0.2)',
                            '&:hover': {
                                backgroundColor: '#009DC0',
                                boxShadow: '0px 6px 16px rgba(0, 180, 216, 0.3)',
                                transform: 'translateY(-1px)'
                            },
                        }}
                    >
                        {isSubmitting ? <CircularProgress size={24} color="inherit" /> : tLocal.saveAndContinue}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default CarbonCreditsPractices;
